import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';
import { Alert, Button, Col, Row, TabContent, TabPane } from 'reactstrap';
import { sumBy } from 'lodash';
import { useScheduling } from '../../../hooks/useScheduling';
import SummaryModal from '../Vendor/Modal';
import BZAPIService from '../../../services/BZAPIService';
import ProductPriority from '../section/ProductPriority';
// constants
import SETTINGS from '../../../constants/settings';
import DefaultConfigTab from '../section/DefaultConfigTab';
import TabNav from '../section/TabNav';
import TabOne from '../section/TabOne';
import RiskScore from '../section/RiskScore';
import HistoryTab from '../section/HistoryTab';
import { animateScroll } from 'react-scroll';
import AITab from '../section/AITab';
const ConfigFormMSFTSqlServer = ({
  vendor,
  isReadOnly,
  settings,
  vendorLabel,
  bugsDateBack,
  setBugsDateBack,
  vendorId,
  vendorPriorityOptions,
  vendorStatusOptions,
  vendorBugCategories,
  setVendorBugCategories,
  vendorBugCategoryOptions,
  snIntegrationCheck,
  services,
  vendors,
  bugs,
  priorityStats,
  setPriorityStats,
  bugOptions,
  selectedBugOption,
  setSelectedBugOption,
  snCompanySysIdOptions,
  snCompanySysIdErrors,
  setVendorStatuses,
  vendorStatuses,
  fields,
  handleFieldChange,
  priorityMaps,
  setPriorityMaps,
  snPriorityOptions,
  keywords,
  setKeywords,
  searchFields,
  setSearchFields,
  snCompanySysId,
  setSnCompanySysId,
  setServices,
  setVendors,
  buildVendorStat,
  setBugs,
  isConfigured,
  syncOnly,
  setSyncOnly,
  loading,
  bugRisk,
  setBugRisk,
  isServiceActive,
  setIsServiceActive,
  allRequired,
  setAllRequired,
  isUpdated,
  setIsUpdated,
  enableChatGptPriority,
  initialEnableChatGptPriority,
  onAiEnabledChange
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  //  const [config, setConfig] = useState(null);

  const [activeTab, setActiveTab] = useState('1');
  const [modalOpen, setModalOpen] = useState(false);
  const [validated, setValidated] = useState(null);
  const [isForceRun, setIsForceRun] = useState(false);

  const [riskError, setRiskError] = useState('');

  // Detect AI priority mode change
  const priorityModeChanged = enableChatGptPriority !== initialEnableChatGptPriority;

  // Validate priorities match the current AI mode
  const priorityModeError = useMemo(() => {
    if (!priorityModeChanged) return '';
    if (!priorityMaps?.length) return 'Please add priorities that match the selected AI mode.';
    if (enableChatGptPriority) {
      const hasAiPriority = priorityMaps.some(m => m.vendorPriority?.isCustom === true);
      if (!hasAiPriority) return 'AI priorities are enabled. Please select at least one AI priority level.';
    } else {
      const hasUnspecified = priorityMaps.some(
        m => m.vendorPriority?.value === 'Unspecified' || m.vendorPriority?.label === 'Unspecified'
      );
      if (!hasUnspecified) return 'AI priorities are disabled. Please include "Unspecified" in the priority selection.';
    }
    return '';
  }, [priorityModeChanged, priorityMaps, enableChatGptPriority]);

  // Use scheduling hook for state and save function
  const {
    timeUnit,
    setTimeUnit,
    timeValue,
    setTimeValue,
    schedulingMode,
    setSchedulingMode,
    schedules,
    setSchedules,
    nextRun,
    setNextRun,
    saveBugServiceFrequency
  } = useScheduling();

  async function setBugServiceFrequency() {
    await saveBugServiceFrequency(vendorId);
  }

  async function saveConfig() {
    // save client config
    const vendorPriorities = priorityMaps.map(mapping => {
      return {
        value: mapping.vendorPriority.value,
        label: mapping.vendorPriority.value,
        isCustom: mapping.vendorPriority.isCustom,
        snPriority: mapping.snPriority.value
      };
    });

    const data = {
      bugsDateBack: bugsDateBack,
      snCompanySysId: snCompanySysId.value ? snCompanySysId.value : fields.snCompanySysId,
      snAffectedCIQuery: fields.snAffectedCIQuery,
      vendorPriorities,
      vendorBugCategories,
      type: SETTINGS.VENDOR,
      excludedKws: keywords,
      searchFields: searchFields,
      bugRiskScore: bugRisk
    };

    return BZAPIService.saveSetting(vendorId, syncOnly, data, isForceRun);
  }

  async function handleSave() {
    setRiskError('');
    try {
      setIsSubmitted(true);
      setIsLoading(true);
      // Validate
      if (!bugsDateBack.length || !snCompanySysId) {
        setValidated(false);
        setActiveTab('3');
        setIsLoading(false);
        return;
      }
      if (!priorityMaps.length || !vendorBugCategories.length || priorityModeError) {
        setValidated(false);
        setActiveTab('4');
        setIsLoading(false);
        return;
      }

      // Validate Risk Score
      const activeRisk = bugRisk.filter(y => {
        return y.active;
      });
      const activeSum = sumBy(activeRisk, risk => {
        return risk.value;
      });

      if (activeSum < 100 && activeSum > 1) {
        console.log('Bug risk share is less than 100');
        setValidated(false);
        setActiveTab('5');
        setIsLoading(false);
        setRiskError('Bug risk share total must be 100');
        return;
      }

      await saveConfig();
      setBugServiceFrequency();
      setIsLoading(false);
      setIsSubmitted(false);
      setModalOpen(false);
      setIsForceRun(false);
      setActiveTab('1');
      toast.success(`Settings Saved!`);
    } catch (e) {
      setIsLoading(false);
      setValidated(false);
      toast.error('Failed to save.');
      console.log('e', e);
    }
  }

  function handleValidationError() {
    setModalOpen(!modalOpen);
    animateScroll.scrollTo(70);
  }

  useEffect(() => {
    setIsUpdated(true);
    if (
      bugsDateBack?.length &&
      snCompanySysId?.value &&
      priorityMaps?.length &&
      !priorityMaps.some(mapping => !mapping.snPriority || !mapping.vendorPriority) &&
      vendorBugCategories.length &&
      !priorityModeError
    ) {
      setAllRequired(true);
    } else {
      setAllRequired(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fields,
    bugsDateBack,
    priorityStats,
    bugRisk,
    priorityMaps,
    keywords,
    vendorBugCategories,
    searchFields,
    snCompanySysId,
    priorityModeError
  ]);

  return (
    <>
      <TabNav
        aiSupported={vendor?.aiSupported}
        vendorId={vendorId}
        credentials={false}
        activeTab={activeTab}
        snIntegrationCheck={snIntegrationCheck}
        setActiveTab={setActiveTab}
        setIsLoading={setIsLoading}
        isLoading={isLoading}
        setServices={setServices}
        setVendors={setVendors}
        buildVendorStat={buildVendorStat}
        setPriorityStats={setPriorityStats}
        setBugs={setBugs}
        isConfigured={isConfigured}
        hasBugRisk={true}
        loading={loading}
      />

      <TabContent>
        <TabOne
          vendorId={vendorId}
          vendorLabel={vendorLabel}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          snIntegrationCheck={snIntegrationCheck}
          services={services}
          vendors={vendors}
          priorityStats={priorityStats}
          isConfigured={isConfigured}
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          setIsForceRun={setIsForceRun}
          timeUnit={timeUnit}
          setTimeUnit={setTimeUnit}
          timeValue={timeValue}
          setTimeValue={setTimeValue}
          schedulingMode={schedulingMode}
          setSchedulingMode={setSchedulingMode}
          schedules={schedules}
          setSchedules={setSchedules}
          nextRun={nextRun}
          setNextRun={setNextRun}
          loading={loading}
          isServiceActive={isServiceActive}
          setIsServiceActive={setIsServiceActive}
        />
        <form noValidate style={{ minWidth: '100%' }}>
          <input type="hidden" value="prayer" />

          <DefaultConfigTab
            vendor={vendor}
            activeTab={activeTab}
            vendorLabel={vendorLabel}
            bugsDateBack={bugsDateBack}
            setBugsDateBack={setBugsDateBack}
            isSubmitted={isSubmitted}
            snCompanySysIdErrors={snCompanySysIdErrors}
            snCompanySysIdOptions={snCompanySysIdOptions}
            setSnCompanySysId={setSnCompanySysId}
            snCompanySysId={snCompanySysId}
            fields={fields}
            handleFieldChange={handleFieldChange}
          />

          <TabPane tabId="4">
            {activeTab === '4' && priorityModeChanged && (
              <Alert color={priorityModeError ? 'danger' : 'success'} className="mb-3">
                {priorityModeError
                  ? `Priority mode changed. ${priorityModeError}`
                  : 'Priority mode changed. Priority selection is valid for the current AI mode.'}
              </Alert>
            )}
            {activeTab === '4' && (
              <ProductPriority
                title="Default Bug Configuration"
                vendorPriorityOptions={vendorPriorityOptions}
                vendorStatusOptions={vendorStatusOptions}
                setVendorStatuses={setVendorStatuses}
                setVendorBugCategories={setVendorBugCategories}
                vendorBugCategories={vendorBugCategories}
                vendorBugCategoryOptions={vendorBugCategoryOptions}
                vendorStatuses={vendorStatuses}
                isSubmitted={isSubmitted}
                priorityMaps={priorityMaps}
                setPriorityMaps={setPriorityMaps}
                snPriorityOptions={snPriorityOptions}
                keywords={keywords}
                setKeywords={setKeywords}
                searchFields={searchFields}
                setSearchFields={setSearchFields}
                vendorId={vendorId}
                enableChatGptPriority={enableChatGptPriority}
              />
            )}
          </TabPane>
          <div style={{ minWidth: '100%' }}>
            <TabPane tabId="5">
              {activeTab === '5' && <RiskScore bugRisk={bugRisk} setBugRisk={setBugRisk} riskError={riskError} />}
            </TabPane>
          </div>
          <TabPane tabId="6">{activeTab === '6' && <HistoryTab vendorId={vendorId} />}</TabPane>
          <TabPane tabId="8">
            {activeTab === '8' && (
              <AITab vendorId={vendorId} isReadOnly={isReadOnly} onAiEnabledChange={onAiEnabledChange} />
            )}
          </TabPane>

          <Row className={`${activeTab === '1' ? 'd-none' : ''} flex-between-center`}>
            <Col xs="auto">
              <Button
                block
                color="falcon-danger"
                onClick={() => {
                  console.log(`/vendors/${vendorId}/configuration`);
                  navigate(-1);
                }}
              >
                Cancel
              </Button>
            </Col>
            <Col xs="auto">
              <SummaryModal
                modalOpen={modalOpen}
                isReadOnly={isReadOnly}
                setModalOpen={setModalOpen}
                handleSave={handleSave}
                isLoading={isLoading}
                vendorLabel={vendorLabel?.replace('Systems', 'Networks')}
                vendorId={vendorId}
                validated={validated}
                setValidated={setValidated}
                // isAddingCredentials={isAddingCredentials}
                syncOnly={syncOnly}
                setSyncOnly={setSyncOnly}
                handleValidationError={handleValidationError}
                allRequired={allRequired}
                isUpdated={isUpdated}
              />
            </Col>
          </Row>
        </form>
      </TabContent>
    </>
  );
};
ConfigFormMSFTSqlServer.propTypes = {
  vendor: PropTypes.object,
  isReadOnly: PropTypes.bool,
  settings: PropTypes.object,
  vendorLabel: PropTypes.string,
  vendorId: PropTypes.string,
  vendorPriorityOptions: PropTypes.array,
  vendorStatusOptions: PropTypes.array,
  vendorResolutionOptions: PropTypes.array,
  snCompanySysIdOptions: PropTypes.object,
  snCompanySysId: PropTypes.object,
  snIntegrationCheck: PropTypes.bool,
  services: PropTypes.array,
  vendors: PropTypes.array,
  bugs: PropTypes.object,
  priorityStats: PropTypes.object,
  setPriorityStats: PropTypes.func,
  bugOptions: PropTypes.array,
  selectedBugOption: PropTypes.object,
  setSelectedBugOption: PropTypes.func,
  snCompanySysIdErrors: PropTypes.bool,
  vendorEventScopes: PropTypes.array,
  setVendorEventScopes: PropTypes.func,
  vendorResolutions: PropTypes.array,
  setVendorResolutions: PropTypes.func,
  setVendorStatuses: PropTypes.func,
  vendorStatuses: PropTypes.array,
  fields: PropTypes.object,
  handleFieldChange: PropTypes.func,
  priorityMaps: PropTypes.array,
  setPriorityMaps: PropTypes.func,
  snPriorityOptions: PropTypes.array,
  keywords: PropTypes.array,
  setKeywords: PropTypes.func,
  searchFields: PropTypes.array,
  setSearchFields: PropTypes.func,
  setSnCompanySysId: PropTypes.func,
  bugsDateBack: PropTypes.string,
  setBugsDateBack: PropTypes.func,
  setServices: PropTypes.func,
  setVendors: PropTypes.func,
  buildVendorStat: PropTypes.func,
  setBugs: PropTypes.func,
  isConfigured: PropTypes.bool,
  syncOnly: PropTypes.bool,
  setSyncOnly: PropTypes.func,
  vendorBugCategories: PropTypes.array,
  setVendorBugCategories: PropTypes.func,
  vendorBugCategoryOptions: PropTypes.array,
  loading: PropTypes.bool,
  bugRisk: PropTypes.array,
  setBugRisk: PropTypes.func,
  setIsServiceActive: PropTypes.func,
  isServiceActive: PropTypes.number,
  allRequired: PropTypes.bool,
  isUpdated: PropTypes.bool,
  setAllRequired: PropTypes.func,
  setIsUpdated: PropTypes.func,
  enableChatGptPriority: PropTypes.bool,
  initialEnableChatGptPriority: PropTypes.bool,
  onAiEnabledChange: PropTypes.func
};
export default ConfigFormMSFTSqlServer;
